import { ChangeEvent, FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { isNotEmpty } from '../../../../../_metronic/helpers'
import { initialPaymentMethod, PaymentMethod } from '../core/_models'
import { useListView } from '../core/ListViewProvider'
import { PaymentMethodsListLoading } from '../components/loading/PaymentMethodsListLoading'
import { createPaymentMethod, updatePaymentMethod } from '../core/_requests'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { FormattedMessage, useIntl } from 'react-intl'
import axios from 'axios'

type Props = {
  isPaymentMethodLoading: boolean
  paymentmethod: PaymentMethod
}

const editPaymentMethodSchema = Yup.object().shape({
  method_name: Yup.string().required('Method name is required'),
  account_details: Yup.string().nullable(),
  account_image: Yup.mixed(),
  status: Yup.boolean().required('Status is required'),
})

const PaymentMethodEditModalForm: FC<Props> = ({ paymentmethod, isPaymentMethodLoading }) => {
  const API_URL = process.env.REACT_APP_THEME_API_URL
  const intl = useIntl()

  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const [paymentmethodForEdit] = useState<PaymentMethod>({
    ...paymentmethod,
    method_name: paymentmethod.method_name || initialPaymentMethod.method_name,
    account_details: paymentmethod.account_details || initialPaymentMethod.account_details,
    account_image: paymentmethod.account_image || initialPaymentMethod.account_image,
    status: paymentmethod.status ?? initialPaymentMethod.status,
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: paymentmethodForEdit,
    validationSchema: editPaymentMethodSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updatePaymentMethod(values)
        } else {
          await createPaymentMethod(values)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0]
      const imageURL = URL.createObjectURL(file)
      setSelectedImage(imageURL)
      formik.setFieldValue('account_image', file)
    }
  }

  return (
    <>
      <form id='kt_modal_add_paymentmethod_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_paymentmethod_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_paymentmethod_header'
          data-kt-scroll-wrappers='#kt_modal_add_paymentmethod_scroll'
          data-kt-scroll-offset='300px'
        >
          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'>
                <FormattedMessage id='PAYMENTMETHOD.FORM.INPUT.METHODNAME' defaultMessage='Method Name' />
              </label>
              <input
                placeholder={intl.formatMessage({ id: 'PAYMENTMETHOD.FORM.PLACEHOLDER.METHODNAME', defaultMessage: 'Enter method name' })}
                {...formik.getFieldProps('method_name')}
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0',
                  { 'is-invalid': formik.touched.method_name && formik.errors.method_name },
                  { 'is-valid': formik.touched.method_name && !formik.errors.method_name }
                )}
                type='text'
                autoComplete='off'
                disabled={formik.isSubmitting || isPaymentMethodLoading}
              />
              {formik.touched.method_name && formik.errors.method_name && (
                <div className='fv-plugins-message-container'>
                  <span role='alert'>{formik.errors.method_name}</span>
                </div>
              )}
            </div>

            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>
                <FormattedMessage id='PAYMENTMETHOD.FORM.INPUT.ACCOUNTDETAILS' defaultMessage='Account Details' />
              </label>
              <textarea
                {...formik.getFieldProps('account_details')}
                className='form-control form-control-solid'
                autoComplete='off'
                placeholder={intl.formatMessage({ id: 'PAYMENTMETHOD.FORM.PLACEHOLDER.ACCOUNTDETAILS', defaultMessage: 'Enter account details' })}
                disabled={formik.isSubmitting || isPaymentMethodLoading}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='d-block fw-bold fs-6 mb-5'>
                <FormattedMessage id='PAYMENTMETHOD.FORM.INPUT.ACCOUNTIMAGE' defaultMessage='Account Image' />
              </label>
              <div
                className='image-input image-input-outline'
                style={{ backgroundImage: `url('${selectedImage ? selectedImage : paymentmethod.account_image}')` }}
              >
                <div className='image-input-wrapper w-125px h-125px' style={{ backgroundImage: `url('${selectedImage ? selectedImage : paymentmethod.account_image}')` }}></div>

                <label
                  className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                  data-kt-image-input-action='change'
                  data-bs-toggle='tooltip'
                  title={intl.formatMessage({ id: 'PAYMENTMETHOD.FORM.INPUT.CHANGEIMAGE', defaultMessage: 'Change image' })}
                >
                  <i className='bi bi-pencil-fill fs-7'></i>
                  <input type='file' name='account_image' accept='.png, .jpg, .jpeg' onChange={handleImageChange} />
                </label>

                <span
                  className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                  data-kt-image-input-action='cancel'
                  data-bs-toggle='tooltip'
                  title={intl.formatMessage({ id: 'PAYMENTMETHOD.FORM.INPUT.CANCELIMAGE', defaultMessage: 'Cancel image' })}
                >
                  <i className='bi bi-x fs-2'></i>
                </span>
              </div>
            </div>

            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>
                <FormattedMessage id='PAYMENTMETHOD.FORM.INPUT.STATUS' defaultMessage='Status' />
              </label>
              <select
                {...formik.getFieldProps('status')}
                className='form-select form-control'
                disabled={formik.isSubmitting || isPaymentMethodLoading}
              >
                <option value='1'>{intl.formatMessage({ id: 'FORM.GENERAL.ACTIVE', defaultMessage: 'Active' })}</option>
                <option value='0'>{intl.formatMessage({ id: 'FORM.GENERAL.INACTIVE', defaultMessage: 'Inactive' })}</option>
              </select>
            </div>
          </div>
        </div>

        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-paymentmethods-modal-action='cancel'
            disabled={formik.isSubmitting || isPaymentMethodLoading}
          >
            <FormattedMessage id='FORM.GENERAL.DISCARD' defaultMessage='Discard' />
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-paymentmethods-modal-action='submit'
            disabled={isPaymentMethodLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'><FormattedMessage id='FORM.GENERAL.SUBMIT' defaultMessage='Submit' /></span>
            {(formik.isSubmitting || isPaymentMethodLoading) && (
              <span className='indicator-progress'>
                <FormattedMessage id='FORM.GENERAL.PLEASEWAIT' defaultMessage='Please wait' />
                {' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isPaymentMethodLoading) && <PaymentMethodsListLoading />}
    </>
  )
}

export { PaymentMethodEditModalForm }
